(() => {
  const BUTTON_SELECTOR = '[data-multilikes-api-store-button]';
  const metaobjectsByHandle = new Map();
  const favoriteObjectIds = new Set();
  let loadPromise = null;
  let loadedCustomerId = '';
  let hasLoaded = false;

  const buttons = () => Array.from(document.querySelectorAll(BUTTON_SELECTOR));
  const normalizeHandle = (value) => String(value || '').trim().toLowerCase();

  const fieldMap = (fields) => {
    if (!Array.isArray(fields)) return {};
    return fields.reduce((result, field) => {
      if (field?.key) result[field.key] = field.value ?? field.jsonValue ?? '';
      return result;
    }, {});
  };

  const metaobjectHandle = (item) => normalizeHandle(
    item?.handle || item?.system?.handle || fieldMap(item?.fields).handle
  );

  const updateButton = (button) => {
    const handle = normalizeHandle(button.dataset.metaobjectHandle);
    const metaobject = metaobjectsByHandle.get(handle);
    const isActive = Boolean(metaobject && favoriteObjectIds.has(String(metaobject.id)));
    const title = button.dataset.storeTitle || '店舗';

    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute('aria-label', `${title}をお気に入り${isActive ? 'から削除' : 'に追加'}`);

    if (hasLoaded && !metaobject) {
      button.disabled = true;
      button.setAttribute('aria-label', `${title}のメタオブジェクト設定を確認してください`);
    } else if (!button.classList.contains('is-loading')) {
      button.disabled = false;
    }
  };

  const updateButtons = (handle = '') => {
    buttons().forEach((button) => {
      if (!handle || normalizeHandle(button.dataset.metaobjectHandle) === handle) updateButton(button);
    });
  };

  const requestUrl = (button) => {
    const url = new URL(button.dataset.multilikesEndpoint || '/apps/multilikes-endpoint', window.location.origin);
    const customerId = button.dataset.customerId || '';
    if (customerId) url.searchParams.set('customer_id', customerId);
    url.searchParams.set('metaobject_type', button.dataset.metaobjectType || 'multilikes_store');
    url.searchParams.set('limit', '250');
    return url;
  };

  const loadStores = async (button, { force = false } = {}) => {
    const customerId = button.dataset.customerId || '';
    if (!force && loadPromise && loadedCustomerId === customerId) return loadPromise;

    loadedCustomerId = customerId;
    loadPromise = (async () => {
      const response = await fetch(requestUrl(button), { headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Store wishlist API request failed');

      metaobjectsByHandle.clear();
      favoriteObjectIds.clear();
      (Array.isArray(result.data) ? result.data : []).forEach((item) => {
        const handle = metaobjectHandle(item);
        if (!handle || !item?.id) return;
        metaobjectsByHandle.set(handle, item);
        if (item.isInWishlist) favoriteObjectIds.add(String(item.id));
      });
      hasLoaded = true;
      updateButtons();
    })().catch((error) => {
      loadPromise = null;
      hasLoaded = false;
      console.error('MultiLikes store status request failed:', error);
      throw error;
    });

    return loadPromise;
  };

  const toggleFavorite = async (button) => {
    const customerId = button.dataset.customerId || '';
    if (!customerId) {
      window.location.assign(button.dataset.loginUrl || '/account/login');
      return;
    }

    // 送信直前に最新状態を取り直す。ボタンの見た目だけが未登録に戻っている場合に
    // 登録済みと判定して remove を送ってしまうのを防ぐ
    try {
      await loadStores(button, { force: true });
    } catch (_) {
      return;
    }

    const handle = normalizeHandle(button.dataset.metaobjectHandle);
    const metaobject = metaobjectsByHandle.get(handle);
    if (!metaobject?.id) {
      console.error(`MultiLikes store metaobject was not found for handle: ${handle}`);
      updateButton(button);
      return;
    }

    const objectId = String(metaobject.id);
    const isActive = favoriteObjectIds.has(objectId);
    const body = new URLSearchParams({
      customer_id: customerId,
      metaobject_type: button.dataset.metaobjectType || 'multilikes_store',
      target_type: 'metaobject',
      metaobject_id: objectId,
      action: isActive ? 'remove' : 'add'
    });

    button.classList.add('is-loading');
    button.disabled = true;
    try {
      const response = await fetch(button.dataset.multilikesEndpoint || '/apps/multilikes-endpoint', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Store wishlist update failed');

      const responseState = result?.data?.isInWishlist;
      const nextState = typeof responseState === 'boolean' ? responseState : !isActive;
      if (nextState) favoriteObjectIds.add(objectId);
      else favoriteObjectIds.delete(objectId);
      updateButtons(handle);
    } catch (error) {
      console.error('MultiLikes store update failed:', error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    toggleFavorite(button);
  });

  const initialize = () => {
    const button = document.querySelector(BUTTON_SELECTOR);
    if (!button) return;
    updateButtons();
    loadStores(button).catch(() => {});
  };

  // セクション再描画などでボタンが差し替わると、サーバー側は常に未登録の状態で
  // 出力するため見た目が戻ってしまう。追加されたボタンを検知して塗り直す
  let updateScheduled = false;
  const scheduleUpdate = () => {
    if (updateScheduled) return;
    updateScheduled = true;
    requestAnimationFrame(() => {
      updateScheduled = false;
      updateButtons();
    });
  };

  const observeButtons = () => {
    if (!document.body) return;
    new MutationObserver((mutations) => {
      const hasNewButton = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.(BUTTON_SELECTOR) || node.querySelector?.(BUTTON_SELECTOR))
        )
      );
      if (hasNewButton) scheduleUpdate();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
      observeButtons();
    }, { once: true });
  } else {
    initialize();
    observeButtons();
  }

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('cart:updated', scheduleUpdate);
})();
